import { SALE_DOUBLE_CLICK_MS, type StockLocation } from './constants';
import { dateOf, isoToday, timeOf, type IsoDate } from './dates';
import { lineTotal, money, num, parseDecimal, roundQty, sumMoney } from './money';
import { nameHash32, normalizedProductName, type CatalogProduct } from './search';

export type SaleLineKind = 'stock' | 'manual' | 'history';

export interface SaleCartLine {
  key: string;
  kind: SaleLineKind;
  productId: string | null;
  name: string;
  code: string;
  unit: string;
  qty: number;
  price: number;
  cost: number;
  /** total stock (local + warehouse) captured when the line was added — prototype caps qty at p.qty */
  stockCap: number | null;
}

export const POS_TEXT = {
  ready: 'Gati për shitje',
  readyHint: 'Skano barkodin ose shkruaj emrin e produktit.',
  notFound: 'Produkti nuk u gjet',
  notFoundHint: 'Mund ta shtosh menjëherë si artikull jashtë stokut.',
  addManually: (q: string) => 'Shto “' + q + '” manualisht',
  moreResults: (n: number) => 'Edhe ' + n + ' rezultate · shkruaj edhe një shkronjë për kërkim më të saktë.',
  barcodeNotFound: 'Produkti me këtë barkod nuk u gjet',
  noStock: 'Produkti nuk ka stok',
  notEnough: 'Nuk ka sasi të mjaftueshme në stok',
  manualName: 'Shkruaj emrin e artikullit',
  manualPrice: 'Shkruaj çmimin e artikullit',
  manualAdded: (name: string) => name + ' u shtua si artikull manual',
  historyMissing: 'Artikulli nuk u gjet në historikun e shitjeve',
  historyAdded: (name: string) => name + ' u shtua nga historiku',
  addOne: 'Shto së paku një produkt',
  productMissing: (name: string) => 'Produkti nuk u gjet: ' + name,
  /** Same text as complete_sale raises, so the till and the server say the same thing. */
  insufficient: (name: string, local: number, warehouse: number) =>
    'Nuk ka stok të mjaftueshëm për ' + name + ' (lokal: ' + num(local) + ', depo: ' + num(warehouse) + ')',
  /** NEW — a sale that took some of its stock from the other place; somebody has to go and fetch it. */
  pulledFrom: (lines: string, location: 'local' | 'warehouse') =>
    lines + (location === 'warehouse' ? ' u zbrit nga depoja' : ' u zbrit nga lokali'),
  chooseWorker: 'Zgjedh Florin ose Xhevatin për dorëzim',
  writeGoods: 'Shkruaj mallin që duhet me u dorëzu',
  savedPrintDelivery: (worker: string) => 'Shitja u ruajt · ' + worker + ' u caktua për dorëzim',
  savedPrint: 'Shitja u ruajt · fatura është gati për printim',
  savedDelivery: (worker: string) => 'Shitja u ruajt · porosia u dërgua te ' + worker,
  savedOutside: 'Shitja u ruajt · artikujt jashtë stokut nuk e prekën stokun',
  savedStock: 'Shitja u ruajt dhe stoku u përditësua',
  deliveryDone: 'Porosia u shënua si e dorëzuar',
  deliveryPending: 'Në pritje',
  deliveryDelivered: 'Dorëzuar',
  deliveryWaiting: 'Pret dorëzimin',
  pendingCount: (n: number) => n + ' në pritje',
  queueEmpty: 'Ende nuk ka porosi për dorëzim.',
  defaultCustomer: 'Klient',
  logSale: 'U përfundua një shitje',
  logDeliveryCreated: (worker: string) => 'Porosi për dorëzim te ' + worker,
  logDeliveryDone: 'U dorëzua porosia',
  receiptTitle: 'FATURË SHITJEJE',
  receiptThanks: 'Faleminderit për blerjen!',
  /** NEW — cash handed over and the change given back, on the till and the receipt. */
  /** NEW — where a delivery is taken from, on the till, the queue and the worker's page. */
  deliveryFrom: { warehouse: 'Nga depoja', local: 'Nga dyqani' } as Record<StockLocation, string>,
  cashGiven: 'Paguar',
  cashChange: 'Kusuri',
  changeNote: (change: number) => 'Kusuri: ' + money(change),
} as const;

export const DOUBLE_CLICK_GUARD_MS = SALE_DOUBLE_CLICK_MS;

/** Shortest retail barcode symbology (EAN-8); the codes this ERP issues itself are 12 digits. */
export const SCANNED_CODE_MIN_LENGTH = 8;

/**
 * A query that can only have come from the barcode scanner: digits only and at least EAN-8 long. A typed
 * product name never matches, so a code the wedge left in the visible search field (instead of #saleScan)
 * still toasts `barcodeNotFound` rather than opening the manual-item form.
 */
export function looksLikeScannedCode(query: string): boolean {
  const code = String(query || '').trim();
  return code.length >= SCANNED_CODE_MIN_LENGTH && /^\d+$/.test(code);
}

/** Prototype `saleQtyText(v)`: integers plain, otherwise up to 2 decimals without trailing zeros. */
export function saleQtyText(v: number | null | undefined): string {
  const n = Number(v || 0);
  return Number.isInteger(n) ? String(n) : String(Number(n.toFixed(2)));
}

/** Prototype `defaultSaleDeliveryText()`: `2 × Pleh + 1 × Tel`. */
export function defaultSaleDeliveryText(cart: readonly SaleCartLine[]): string {
  return cart.map((i) => saleQtyText(i.qty) + ' × ' + i.name).join(' + ');
}

export function saleCartTotal(cart: readonly SaleCartLine[]): number {
  return Math.round(cart.reduce((s, i) => s + i.qty * i.price, 0) * 100) / 100;
}

export function saleCartCount(cart: readonly SaleCartLine[]): number {
  return cart.length;
}

/** Prototype `addSaleItem(id)`: +1 of a stock product, capped at total stock. */
export function addSaleItem(
  cart: readonly SaleCartLine[],
  p: CatalogProduct,
): { cart: SaleCartLine[]; error: string | null } {
  if (p.total <= 0) return { cart: [...cart], error: POS_TEXT.noStock };
  const x = cart.find((i) => i.kind === 'stock' && i.productId === p.id);
  if (x) {
    if (x.qty >= p.total) return { cart: [...cart], error: POS_TEXT.notEnough };
    return {
      cart: cart.map((i) => (i === x ? { ...i, qty: i.qty + 1, stockCap: p.total } : i)),
      error: null,
    };
  }
  return {
    cart: [
      ...cart,
      {
        key: p.id,
        kind: 'stock',
        productId: p.id,
        name: p.name,
        code: p.code,
        unit: p.unit,
        qty: 1,
        price: p.sell,
        cost: Number(p.buy || 0),
        stockCap: p.total,
      },
    ],
    error: null,
  };
}

/** Prototype `addManualSaleItem()`: merges with a manual line of the same normalized name and price. */
export function addManualSaleItem(
  cart: readonly SaleCartLine[],
  input: { name: string; qty: number | null; price: number | null; code?: string },
  newKey: () => string,
): { cart: SaleCartLine[]; error: string | null; toast: string | null } {
  const name = (input.name || '').trim();
  // NEW — the article's own barcode, when it has one: printed in the reports and found again on a scan
  const code = (input.code || '').trim();
  const qty = Math.max(0.01, Number(input.qty || 1));
  const price = Math.max(0, Number(input.price || 0));
  if (!name) return { cart: [...cart], error: POS_TEXT.manualName, toast: null };
  if (!(price > 0)) return { cart: [...cart], error: POS_TEXT.manualPrice, toast: null };
  const norm = normalizedProductName(name);
  const existing = cart.find(
    (x) =>
      x.kind === 'manual' &&
      normalizedProductName(x.name) === norm &&
      Number(x.price || 0) === price &&
      (x.code || '') === code,
  );
  const next = existing
    ? cart.map((x) => (x === existing ? { ...x, qty: Number(x.qty || 0) + qty } : x))
    : [
        ...cart,
        {
          key: newKey(),
          kind: 'manual' as const,
          productId: null,
          name,
          code,
          unit: '',
          qty,
          price,
          cost: 0,
          stockCap: null,
        },
      ];
  return { cart: next, error: null, toast: POS_TEXT.manualAdded(name) };
}

export interface HistoryItem {
  key: string;
  name: string;
  code: string;
  unit: string;
  price: number;
  cost: number;
  lastDate: string;
  count: number;
}

export interface SaleItemRecord {
  product_id: string | null;
  kind: SaleLineKind;
  name: string;
  code: string;
  unit: string;
  qty: number;
  price: number;
  cost: number;
}

export interface SaleRecord {
  id: string;
  ref: string;
  sold_at: string;
  total: number;
  source: 'shop' | 'warehouse';
  items: SaleItemRecord[];
  /** Cash the customer handed over, when the cashier typed it; printed under the total. */
  cash?: { given: number; change: number };
}

/** Prototype `saleHistoryCatalog()`: one entry per normalized item name, keeping the most recent price. */
export function saleHistoryCatalog(
  sales: readonly SaleRecord[],
  products: readonly CatalogProduct[],
): HistoryItem[] {
  const map = new Map<string, HistoryItem>();
  const byId = new Map(products.map((p) => [p.id, p]));
  for (const s of sales) {
    const stamp = dateOf(s.sold_at);
    for (const i of s.items) {
      const name = String(i.name || '').trim();
      if (!name) continue;
      const key = normalizedProductName(name);
      if (!key) continue;
      const old = map.get(key);
      if (!old || stamp >= old.lastDate) {
        const p = i.product_id ? byId.get(i.product_id) : undefined;
        map.set(key, {
          key,
          name,
          code: String(i.code || p?.code || ''),
          unit: String(p?.unit || 'copë'),
          price: Number(i.price || p?.sell || 0),
          cost: Number(i.cost || p?.buy || 0),
          lastDate: stamp,
          count: (old?.count || 0) + 1,
        });
      } else old.count = (old.count || 0) + 1;
    }
  }
  return [...map.values()];
}

export function historyLineKey(key: string): string {
  return 'hist_' + nameHash32(key);
}

/** Prototype `addHistoricSaleItem(key)`: prefers the stock product when it has stock, else adds an out-of-stock line. */
export function addHistoricSaleItem(
  cart: readonly SaleCartLine[],
  h: HistoryItem | null,
  products: readonly CatalogProduct[],
): {
  cart: SaleCartLine[];
  error: string | null;
  toast: string | null;
  useProduct: CatalogProduct | null;
} {
  if (!h)
    return {
      cart: [...cart],
      error: POS_TEXT.historyMissing,
      toast: null,
      useProduct: null,
    };
  const stockProduct = products.find(
    (p) => normalizedProductName(p.name) === h.key && Number(p.total || 0) > 0,
  );
  if (stockProduct)
    return {
      cart: [...cart],
      error: null,
      toast: null,
      useProduct: stockProduct,
    };
  const id = historyLineKey(h.key);
  const x = cart.find((i) => i.kind === 'history' && i.key === id);
  const next = x
    ? cart.map((i) => (i === x ? { ...i, qty: i.qty + 1 } : i))
    : [
        ...cart,
        {
          key: id,
          kind: 'history' as const,
          productId: null,
          name: h.name,
          code: h.code || '',
          unit: h.unit,
          qty: 1,
          price: Number(h.price || 0),
          cost: Number(h.cost || 0),
          stockCap: null,
        },
      ];
  return {
    cart: next,
    error: null,
    toast: POS_TEXT.historyAdded(h.name),
    useProduct: null,
  };
}

/** NEW — the smallest quantity a line takes: goods sold by weight or length go down to 0.01 (0,50 kg). */
export const MIN_SALE_QTY = 0.01;

const roundSaleQty = (n: number) => Math.round(n * 100) / 100;

/**
 * Prototype `changeSaleQty(id, n)`: manual/history lines ≥ 0.01; stock lines up to the stock. NEW — − stops at
 * 1 or at a part of a unit already typed (0,50 kg stays 0,50), never raising a stock line to 1.
 */
export function changeSaleQty(
  cart: readonly SaleCartLine[],
  key: string,
  n: number,
  stockOf: (productId: string) => number,
): SaleCartLine[] {
  return cart.map((x) => {
    if (x.key !== key) return x;
    const qty = Number(x.qty);
    if (x.kind !== 'stock') return { ...x, qty: Math.max(MIN_SALE_QTY, roundSaleQty(qty + n)) };
    const cap = x.productId ? stockOf(x.productId) : 1;
    return {
      ...x,
      qty: Math.max(Math.min(1, qty), Math.min(Number(cap || 1), roundSaleQty(qty + n))),
    };
  });
}

/**
 * Prototype `setSaleQty(id, v)`: the typed quantity, stock lines capped at the stock. NEW — a part of a unit
 * is kept (0,50 kg of onion sets), down to 0.01; nothing or 0 typed is 1, as in the prototype.
 */
export function setSaleQty(
  cart: readonly SaleCartLine[],
  key: string,
  v: number | null,
  stockOf: (productId: string) => number,
): SaleCartLine[] {
  return cart.map((x) => {
    if (x.key !== key) return x;
    const n = v && v > 0 ? Math.max(MIN_SALE_QTY, roundSaleQty(v)) : 1;
    if (x.kind !== 'stock') return { ...x, qty: n };
    const cap = x.productId ? stockOf(x.productId) : 1;
    return { ...x, qty: Math.min(Number(cap || 1), n) };
  });
}

/** Prototype `setSalePrice(id, v)`. */
export function setSalePrice(cart: readonly SaleCartLine[], key: string, v: number | null): SaleCartLine[] {
  return cart.map((x) => (x.key === key ? { ...x, price: Math.max(0, Number(v || 0)) } : x));
}

/**
 * NEW — the barcode of an article the shop does not stock (a manual or history line). A stock line keeps its
 * product's own barcode; that one is changed on the product, not on a sale.
 */
export function setSaleCode(cart: readonly SaleCartLine[], key: string, code: string): SaleCartLine[] {
  return cart.map((x) => (x.key === key && x.kind !== 'stock' ? { ...x, code: code.trim() } : x));
}

/** NEW — an article sold before without being in stock, found by the barcode it was sold with. */
export function findHistoryByCode(history: readonly HistoryItem[], code: string): HistoryItem | null {
  const c = code.trim();
  if (!c) return null;
  return history.find((h) => h.code === c) ?? null;
}

export function removeSaleItem(cart: readonly SaleCartLine[], key: string): SaleCartLine[] {
  return cart.filter((x) => x.key !== key);
}

export interface DeliveryDraft {
  workerEmployeeId: string | null;
  workerName: string;
  customer: string;
  note: string;
  /** NEW — where the worker takes the goods from: the depo (as before) or the shop. */
  from: StockLocation;
  /** NEW — where the goods go; optional, shown on the worker's card and in the notification. */
  address: string;
}

/** Prototype `saleDeliveryDraft()`: null when disabled, error text when invalid, else the draft. */
export function validateDeliveryDraft(input: {
  enabled: boolean;
  workerEmployeeId: string | null;
  workerName: string;
  customer: string;
  note: string;
  from?: StockLocation;
  address?: string;
}): { delivery: DeliveryDraft | null; error: string | null } {
  if (!input.enabled) return { delivery: null, error: null };
  const workerName = (input.workerName || '').trim();
  const customer = (input.customer || '').trim();
  const note = (input.note || '').trim();
  if (!workerName) return { delivery: null, error: POS_TEXT.chooseWorker };
  if (!note) return { delivery: null, error: POS_TEXT.writeGoods };
  return {
    delivery: {
      workerEmployeeId: input.workerEmployeeId,
      workerName,
      customer,
      note,
      from: input.from ?? 'warehouse',
      address: (input.address || '').trim(),
    },
    error: null,
  };
}

/** Prototype completeSale stock pre-check for the chosen place (client side; the RPC repeats it). */
/**
 * Whether the shop and the depo together can cover the cart. Which of the two a unit actually comes
 * from is decided by complete_sale — the natural place first, the rest from the other — so the till
 * only refuses what the business genuinely does not have.
 */
export function validateSaleStock(
  cart: readonly SaleCartLine[],
  lookup: (productId: string) => { name: string; local: number; warehouse: number } | undefined,
): string | null {
  if (!cart.length) return POS_TEXT.addOne;
  for (const i of cart) {
    if (i.kind !== 'stock' || !i.productId) continue;
    const p = lookup(i.productId);
    if (!p) return POS_TEXT.productMissing(i.name);
    const local = Math.max(0, p.local);
    const warehouse = Math.max(0, p.warehouse);
    if (local + warehouse < i.qty) return POS_TEXT.insufficient(i.name, local, warehouse);
  }
  return null;
}

export interface PulledStock {
  name: string;
  qty: number;
  location: 'local' | 'warehouse';
}

/** "1 × Matorra Dongyin 18m u zbrit nga depoja", or '' when every unit came from the natural place. */
export function pulledStockNote(pulled: readonly PulledStock[]): string {
  if (!pulled.length) return '';
  const byPlace = new Map<'local' | 'warehouse', string[]>();
  for (const p of pulled) {
    const list = byPlace.get(p.location) ?? [];
    list.push(num(p.qty) + ' × ' + p.name);
    byPlace.set(p.location, list);
  }
  return [...byPlace.entries()]
    .map(([place, lines]) => POS_TEXT.pulledFrom(lines.join(', '), place))
    .join(' · ');
}

/** Prototype completeSale toasts. */
export function saleCompletedToast(print: boolean, delivery: DeliveryDraft | null, outside: boolean): string {
  if (print) return delivery ? POS_TEXT.savedPrintDelivery(delivery.workerName) : POS_TEXT.savedPrint;
  if (delivery) return POS_TEXT.savedDelivery(delivery.workerName);
  return outside ? POS_TEXT.savedOutside : POS_TEXT.savedStock;
}

/** Prototype double-click guard: ignore a second click within 1.2 s of the last completed sale. */
export function isDoubleClick(lastSavedAt: number, now: number): boolean {
  return now - lastSavedAt < DOUBLE_CLICK_GUARD_MS;
}

/** Receipt reference: the DB `ref` (10 chars), or the last 10 chars of the id upper-cased like the prototype. */
export function saleReceiptReference(
  sale: { ref?: string | null; id?: string | null } | null | undefined,
): string {
  if (sale?.ref) return sale.ref;
  const id = String(sale?.id || '')
    .replace(/^sale_/, '')
    .replace(/-/g, '')
    .toUpperCase();
  return id ? id.slice(-10) : '—';
}

/** Receipt time HH:mm in Europe/Belgrade (prototype `saleReceiptTime`). */
export function saleReceiptTime(sale: { sold_at?: string | null } | null | undefined): string {
  return sale?.sold_at ? timeOf(sale.sold_at) : '';
}

export interface ReceiptLine {
  name: string;
  qtyText: string;
  priceText: string;
  lineText: string;
}

/** Data behind `buildSaleReceiptInner`. */
export function receiptModel(sale: SaleRecord): {
  title: string;
  dateText: string;
  timeText: string;
  reference: string;
  lines: ReceiptLine[];
  totalText: string;
  cashLines: { label: string; text: string }[];
  thanks: string;
} {
  return {
    title: POS_TEXT.receiptTitle,
    dateText: dateOf(sale.sold_at),
    timeText: saleReceiptTime(sale),
    reference: saleReceiptReference(sale),
    lines: sale.items.map((i) => ({
      name: i.name || 'Artikull',
      qtyText: num(i.qty),
      priceText: money(i.price),
      lineText: money(Number(i.qty || 0) * Number(i.price || 0)),
    })),
    totalText: money(sale.total),
    cashLines: sale.cash
      ? [
          { label: POS_TEXT.cashGiven, text: money(sale.cash.given) },
          { label: POS_TEXT.cashChange, text: money(sale.cash.change) },
        ]
      : [],
    thanks: POS_TEXT.receiptThanks,
  };
}

/**
 * NEW — what to give back when the customer pays cash. Counted in cents, so 50 against 19,99 is 30,01 and
 * never 30,009999. `short` is what is still missing when too little was handed over; null when nothing
 * (or nothing sensible) was typed.
 */
export function cashChange(
  total: number,
  given: number | null,
): { given: number; change: number; short: number } | null {
  if (given === null || !Number.isFinite(given) || given <= 0) return null;
  const diff = Math.round(given * 100) - Math.round(total * 100);
  return {
    given: Math.round(given * 100) / 100,
    change: diff > 0 ? diff / 100 : 0,
    short: diff < 0 ? -diff / 100 : 0,
  };
}

const EURO_NOTES = [5, 10, 20, 50, 100] as const;

/**
 * NEW — the amounts a customer most likely hands over for this total: the total rounded up to each note
 * size, above the total itself (paying exactly has its own button), smallest first.
 */
export function suggestedCash(total: number, limit = 4): number[] {
  const cents = Math.round(total * 100);
  if (cents <= 0) return [];
  const out: number[] = [];
  for (const note of EURO_NOTES) {
    const amount = Math.ceil(cents / (note * 100)) * note;
    if (amount * 100 > cents && !out.includes(amount)) out.push(amount);
  }
  return out.slice(0, limit);
}

/** Prototype `saleItemCost(item)`: stored cost when finite, else the product's buy price. */
export function saleItemCost(
  item: { cost: number | null | undefined; product_id: string | null },
  productBuy: (id: string) => number | undefined,
): number {
  const c = Number(item.cost);
  if (Number.isFinite(c) && item.cost !== null && item.cost !== undefined) return c;
  return Number((item.product_id ? productBuy(item.product_id) : 0) || 0);
}

/** Prototype `saleSource(sale)`: warehouse sales (deliveries) are "Magazina", the rest "Shitorja". */
export function saleSource(sale: { source: 'shop' | 'warehouse' }): 'Magazina' | 'Shitorja' {
  return sale.source === 'warehouse' ? 'Magazina' : 'Shitorja';
}

export interface TodaySalesRow {
  name: string;
  code: string;
  source: 'Magazina' | 'Shitorja';
  /** Where it was sold from and which article it is — what return_sale_item needs to find today's lines. */
  place: 'shop' | 'warehouse';
  productId: string | null;
  qty: number;
  revenue: number;
  cost: number;
}

export interface TodaySalesData {
  date: IsoDate;
  sales: SaleRecord[];
  rows: TodaySalesRow[];
  total: number;
  shopTotal: number;
  warehouseTotal: number;
  cost: number;
  qty: number;
  profit: number;
}

/** Prototype `todaySalesData()` — profit = Σ sale totals − Σ item costs (rows grouped by source + product). */
export function todaySalesData(
  allSales: readonly SaleRecord[],
  products: readonly Pick<CatalogProduct, 'id' | 'code' | 'name' | 'buy'>[],
  today: IsoDate = isoToday(),
): TodaySalesData {
  const byId = new Map(products.map((p) => [p.id, p]));
  const sales = allSales.filter((s) => dateOf(s.sold_at) === today);
  const map = new Map<string, TodaySalesRow>();
  for (const s of sales) {
    const source = saleSource(s);
    for (const i of s.items) {
      const key = source + '|' + (i.product_id || i.name);
      const qty = Number(i.qty || 0);
      const revenue = qty * Number(i.price || 0);
      const cost = qty * saleItemCost(i, (id) => byId.get(id)?.buy);
      const p = i.product_id ? byId.get(i.product_id) : undefined;
      const row = map.get(key) ?? {
        name: i.name || p?.name || 'Produkt',
        code: p?.code || i.code || '—',
        source,
        place: s.source,
        productId: i.product_id,
        qty: 0,
        revenue: 0,
        cost: 0,
      };
      row.qty += qty;
      row.revenue += revenue;
      row.cost += cost;
      map.set(key, row);
    }
  }
  const rows = [...map.values()].sort((a, b) => a.source.localeCompare(b.source) || b.revenue - a.revenue);
  const total = sales.reduce((s, x) => s + Number(x.total || 0), 0);
  const shopTotal = sales
    .filter((x) => saleSource(x) === 'Shitorja')
    .reduce((s, x) => s + Number(x.total || 0), 0);
  const warehouseTotal = sales
    .filter((x) => saleSource(x) === 'Magazina')
    .reduce((s, x) => s + Number(x.total || 0), 0);
  const cost = rows.reduce((s, x) => s + x.cost, 0);
  const qty = rows.reduce((s, x) => s + x.qty, 0);
  return {
    date: today,
    sales,
    rows,
    total,
    shopTotal,
    warehouseTotal,
    cost,
    qty,
    profit: total - cost,
  };
}

export const SALE_RETURN_TEXT = {
  action: 'Kthim malli',
  hint: 'Klienti solli mallin prapa? Shtyp “Kthe” te artikulli.',
  /** The button at the end of each row, and its column. */
  button: 'Kthe',
  column: 'Kthim',
  buttonLabel: (name: string) => 'Kthe ' + name,
  qty: 'Sasia që kthehet',
  refund: 'Kthehen klientit',
  confirm: 'Kthe mallin',
  cancel: 'Anulo',
  qtyMissing: 'Shkruaj sasinë që kthehet',
  qtyTooMuch: (sold: number) => 'Sot janë shitur vetëm ' + num(sold) + ' — aq mund të kthehen',
  done: (qty: number, name: string, amount: number) =>
    'Malli u kthye · ' + num(qty) + ' × ' + name + ' · ' + money(amount) + ' për klientin',
} as const;

/** The quantity typed for a return, or the reason it cannot go back (more than was sold today). */
export function parseReturnQty(raw: string, sold: number): { qty: number; error: string | null } {
  const qty = roundQty(parseDecimal(raw) ?? 0);
  if (qty <= 0) return { qty: 0, error: SALE_RETURN_TEXT.qtyMissing };
  if (qty > roundQty(sold)) return { qty, error: SALE_RETURN_TEXT.qtyTooMuch(sold) };
  return { qty, error: null };
}

/**
 * What returning `qty` of a today's-sales row gives back to the customer. Mirrors return_sale_item: the
 * quantity comes off the most recent sale first, each line at the price it was sold for.
 */
export function saleReturnAmount(
  sales: readonly SaleRecord[],
  row: Pick<TodaySalesRow, 'place' | 'productId' | 'name'>,
  qty: number,
): number {
  const newestFirst = sales
    .filter((s) => s.source === row.place)
    .sort((a, b) => Date.parse(b.sold_at) - Date.parse(a.sold_at));
  let left = roundQty(qty);
  const parts: number[] = [];
  for (const sale of newestFirst) {
    for (const item of [...sale.items].reverse()) {
      if (left <= 0) break;
      const same = row.productId
        ? item.product_id === row.productId
        : !item.product_id && item.name === row.name;
      if (!same) continue;
      const take = Math.min(left, Number(item.qty));
      parts.push(lineTotal(take, Number(item.price)));
      left = roundQty(left - take);
    }
  }
  return sumMoney(parts);
}
